/**
 * DisplayDrawer styles
 *
 * Bottom drawer with handle that opens with the panel.
 * Clicking handle toggles open/closed.
 */
export const displayDrawerStyles = `
/* Display Wrapper - fixed position container */
.display-wrapper {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  pointer-events: none; /* Don't block map interactions */
}

/* Handle - always visible at top */
.display-handle {
  background: rgba(255, 255, 255, 0.98);
  padding: 6px 14px;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 2px solid rgba(22, 163, 74, 0.5);
  box-shadow:
    0 -2px 10px rgba(22, 163, 74, 0.25),
    0 0 12px rgba(22, 163, 74, 0.2);
  transition: all 0.2s;
  order: 0; /* Handle above panel */
  pointer-events: auto; /* Handle is always clickable */
}

.display-handle:hover {
  padding-top: 10px;
  box-shadow:
    0 -4px 15px rgba(22, 163, 74, 0.35),
    0 0 16px rgba(22, 163, 74, 0.3);
  border-bottom-color: rgba(22, 163, 74, 0.7);
}

.display-handle-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #333;
}

/* Points up (toward the panel that's about to rise above it) when closed,
   flips to point down (toward the handle's own resting position) when
   open - the transition is what makes the flip itself readable as a state
   change, not just the end position. */
.display-handle-arrow {
  flex-shrink: 0;
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.display-wrapper.open .display-handle-arrow {
  transform: rotate(0deg);
}

.display-handle-icons {
  display: flex;
  gap: 4px;
}

.display-icon {
  font-size: 12px;
  opacity: 0.5;
}

.display-icon.active {
  opacity: 1;
}

/* Text badge naming the active fill (Zoning/Trends/Climate) - deliberately
   text, not another pictogram, so this doesn't turn into a growing set of
   icons nobody can tell apart as more fill types get added. */
.display-icon-badge {
  font-size: 10px;
  font-weight: 600;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.12);
  padding: 2px 6px;
  border-radius: 999px;
  line-height: 1.3;
}

/* Panel - slides up from bottom */
.display-panel {
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 12px 12px 0 0;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.3s ease, opacity 0.2s ease;
  order: 1; /* Panel below handle */
}

.display-wrapper.open .display-panel {
  max-height: 320px;
  opacity: 1;
  pointer-events: auto; /* Panel is interactive when open */
  overflow-y: auto; /* safety net on very short viewports */
}

.display-panel-body {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 280px;
}

.display-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.display-group-title {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  width: 84px;
  flex-shrink: 0;
}

.display-group-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.display-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: opacity 0.15s;
}

.display-option:hover {
  opacity: 0.8;
}

.display-option input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #16a34a;
}

.display-option-label {
  font-size: 0.8rem;
  color: #333;
}

.display-toggle-group {
  display: flex;
  gap: 6px;
}

.display-toggle {
  padding: 5px 10px;
  background: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.display-toggle:hover {
  background: #f3f4f6;
}

.display-toggle.active {
  background: #16a34a;
  border-color: #16a34a;
  color: white;
}

.display-location-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: white;
  color: #333;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.display-location-btn:hover {
  background: #f0fdf4;
  border-color: #22c55e;
  color: #16a34a;
}

.display-location-btn.active {
  background: #e8f0fe;
  border-color: #4285F4;
  color: #1a56db;
}

.display-location-btn.active:hover {
  background: #d9e6fd;
  border-color: #4285F4;
  color: #1a56db;
}

.display-location-icon {
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .display-panel-body {
    min-width: 240px;
  }

  .display-group {
    flex-wrap: wrap;
  }

  .display-group-title {
    width: auto;
  }
}
`;
export function injectStyles() {
    if (document.getElementById('display-drawer-styles'))
        return;
    const style = document.createElement('style');
    style.id = 'display-drawer-styles';
    style.textContent = displayDrawerStyles;
    document.head.appendChild(style);
}
//# sourceMappingURL=styles.js.map